import React from 'react';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { getFilterType, getFilterWidth } from './filterConfig';
import { filterSublabelSx } from '../../../styles';
import TickerIcon from '../../common/TickerIcon';
import {
  formatCompanyDisplayLabel,
  isPrivateCompanyIdentifier,
} from '../../../utils/companyHelpers';

// Filter Components
import CategoryFilter from '../../filters/Category';
import CategoryFlatFilter from '../../filters/CategoryFlat';
import IndexCategoryFilter from '../../filters/IndexCategory';
import AssetClassFilter from '../../filters/AssetClassFilter';
import TickerFilterWrapper from '../../filters/TickerFilterWrapper';
import MetricFilterWrapper from '../../filters/MetricFilterWrapper';
import TableFieldsFilter from '../../filters/TableFields';
import FilterToggle from './FilterToggle';
import IconSwitch from '../../filters/IconSwitch';
import SimpleSelect from '../../filters/SimpleSelect';
import {
  ControlAutocomplete,
  ControlCalculationSelect,
  ControlField,
  ControlFormatToggle,
  ControlGroupedMultiSelect,
  ControlNumberInput,
  ControlPeriodToggle,
  ControlSlider,
  ControlSwitch,
  ControlTimeFrameToggle,
  ControlToggleGroup,
  CONTROL_SLIDER_SIZES,
  getControlTokens,
} from '../controls';

import EnhancedSlider from '../../filters/EnhancedSlider';

/**
 * FilterRenderer - Renders the appropriate filter component based on type
 *
 * @param {object} props
 * @param {object} props.config - Filter configuration object
 * @param {object} props.state - Current filter state
 * @param {object} props.handlers - Filter change handlers
 * @param {boolean} props.isProUser - Whether user has pro subscription
 * @param {function} props.onUpgradeClick - Callback when pro feature is clicked
 */
function FilterRenderer({
  config,
  state,
  handlers,
  isProUser = false,
  onUpgradeClick,
  controlContext: defaultControlContext = 'filterBar',
  showSublabel = true,
}) {
  const theme = useTheme();
  const filterType = getFilterType(config.type);
  const stateKey = config.stateKey || filterType.stateKey || config.id;
  const width = getFilterWidth(config.type, config.width);
  const controlContext = config.context || config.props?.controlContext || config.props?.context || defaultControlContext;
  const menuContext = config.menuContext || config.props?.menuContext || controlContext;
  const tokens = getControlTokens(theme, controlContext);
  const shouldShowSublabel = showSublabel && Boolean(config.sublabel);
  const fieldWidth = config.type === 'enhancedSlider'
    && typeof width === 'number'
    && config.props?.showPresets !== false
    ? width + (config.props?.presetButtonWidth ?? CONTROL_SLIDER_SIZES.presetButtonWidth)
    : width;

  // Get the current value from state
  const value = state[stateKey];

  // Render the appropriate filter component
  const renderFilter = () => {
    switch (config.type) {
      case 'category':
        return (
          <CategoryFilter
            selectedCategory={value || []}
            handleCategoryChange={handlers[`handleSelectedCategoryChange`] || handlers[`handle${capitalize(stateKey)}Change`]}
            hideDiversified={config.props?.hideDiversified || false}
            controlContext={controlContext}
            menuContext={menuContext}
            width={width}
            {...config.props}
          />
        );

      case 'categoryFlat':
        return (
          <CategoryFlatFilter
            checked={value || []}
            handleToggle={handlers[`handleCheckedToggle`] || handlers[`handle${capitalize(stateKey)}Toggle`]}
            items={config.props?.items || []}
            {...config.props}
          />
        );

      case 'indexCategory':
        return (
          <IndexCategoryFilter
            checked={value || []}
            handleToggle={handlers[`handleCheckedToggle`] || handlers[`handle${capitalize(stateKey)}Toggle`]}
            items={config.props?.items || []}
            isPaid={isProUser}
            onRequirePro={onUpgradeClick}
            {...config.props}
          />
        );

      case 'ticker':
        return (
          <TickerFilterWrapper
            allTickers={config.props?.allTickers || []}
            selectedTickers={value || []}
            setSelectedTickers={handlers[`setSelectedTickers`] || handlers[`set${capitalize(stateKey)}`]}
            controlContext={controlContext}
            width={width}
            {...config.props}
          />
        );

      case 'fields':
        return (
          <MetricFilterWrapper
            selectedColumns={value || []}
            handleColumnsChange={handlers[`handleSelectedColumnsChange`] || handlers[`handle${capitalize(config.id)}Change`] || handlers[`handle${capitalize(stateKey)}Change`]}
            setSelectedColumns={handlers[`setSelectedColumns`] || handlers[`set${capitalize(config.id)}`] || handlers[`set${capitalize(stateKey)}`]}
            allColDefs={config.props?.allColDefs || []}
            isProUser={isProUser}
            onUpgradeClick={onUpgradeClick}
            allowMulti={config.props?.allowMulti ?? true}
            placeholderText={config.props?.placeholderText || 'Select Columns'}
            controlContext={controlContext}
            menuContext={menuContext}
            {...config.props}
          />
        );

      case 'slider':
        return (
          <ControlSlider
            context={controlContext}
            label={config.props?.label || config.props?.period || 'Years'}
            labelLines={config.props?.labelLines}
            value={value ?? 8}
            max={config.props?.max || 20}
            min={config.props?.min || 2}
            onChange={handlers[`handleSliderValueChange`] || handlers[`handle${capitalize(stateKey)}Change`]}
            onInputChange={handlers[`handleSliderValueInputChange`] || handlers[`handle${capitalize(stateKey)}InputChange`]}
            onBlur={handlers[`handleSliderValueBlur`] || handlers[`handle${capitalize(stateKey)}Blur`]}
            width={width}
            labelWidth={config.props?.labelWidth}
            sliderWidth={config.props?.sliderWidth}
            inputAriaLabel={config.props?.inputAriaLabel || `${config.sublabel || config.id} value`}
          />
        );

      case 'toggle':
        return (
          <FilterToggle
            value={value || ''}
            onChange={handlers[`handleToggleValueChange`] || handlers[`handle${capitalize(stateKey)}Change`]}
            options={config.props?.options || []}
            toggleLabel={config.props?.toggleLabel || 'Toggle View'}
            width={config.props?.toggleWidth || 125}
            {...config.props}
          />
        );

      case 'controlToggle':
        return (
          <ControlToggleGroup
            context={controlContext}
            value={value || ''}
            onChange={(e, newVal) => {
              if (newVal === null) return;
              const handler = handlers[`handle${capitalize(stateKey)}Change`];
              const setter = handlers[`set${capitalize(stateKey)}`];
              if (handler) handler(e, newVal);
              else if (setter) setter(newVal);
            }}
            options={config.props?.options || []}
            label={config.props?.label}
            labelWidth={config.props?.labelWidth ?? 0}
            buttonWidth={config.props?.buttonWidth}
            buttonHeight={config.props?.buttonHeight}
            mode={config.props?.mode || 'icon'}
            ariaLabel={config.props?.ariaLabel || config.props?.label || config.id}
            lockedOptions={config.props?.lockedOptions}
            hasLockedAccess={config.props?.hasLockedAccess}
            onLockedOptionSelect={config.props?.onLockedOptionSelect || onUpgradeClick}
            lockedTooltip={config.props?.lockedTooltip}
            sx={config.props?.sx}
          />
        );

      case 'groupedMultiSelect':
        return (
          <ControlGroupedMultiSelect
            context={controlContext}
            menuContext={menuContext}
            groups={config.props?.groups || []}
            value={value || {}}
            onChange={(nextValue) => {
              const handler = handlers[`handle${capitalize(stateKey)}Change`];
              const setter = handlers[`set${capitalize(stateKey)}`];
              if (handler) handler(nextValue);
              else if (setter) setter(nextValue);
            }}
            width={config.props?.width || config.width || 210}
            placeholder={config.props?.placeholder}
            allSelectedLabel={config.props?.allSelectedLabel}
            selectedLabel={config.props?.selectedLabel}
            ariaLabel={config.props?.ariaLabel || config.sublabel || config.id}
            sx={config.props?.sx}
          />
        );

      case 'number':
        return (
          <ControlNumberInput
            context={controlContext}
            value={value}
            label={config.props?.label || 'n'}
            min={config.props?.min ?? 0}
            step={config.props?.step ?? 1}
            defaultValue={config.props?.defaultValue ?? 0}
            maxDigits={config.props?.maxDigits ?? 4}
            onChange={(e) => {
              const handler = handlers[`handle${capitalize(stateKey)}Change`] || handlers[`set${capitalize(stateKey)}`];
              if (handler) handler(Math.max(config.props?.min ?? 0, Number(e.target.value || 0)));
            }}
            onFocus={(e) => e.target.select()}
            onKeyDown={(e) => {
              if ((value ?? 0) === 0 && /^[0-9]$/.test(e.key)) {
                e.preventDefault();
                const handler = handlers[`handle${capitalize(stateKey)}Change`] || handlers[`set${capitalize(stateKey)}`];
                if (handler) handler(Number(e.key));
              }
            }}
            textFieldProps={config.props?.textFieldProps}
          />
        );

      case 'status':
        return (
          <TableFieldsFilter
            selectedColumns={value || []}
            setSelectedColumns={handlers[`set${capitalize(stateKey)}`] || handlers[`setStatus`]}
            handleColumnsChange={handlers[`handle${capitalize(stateKey)}Change`] || handlers[`handleStatusChange`]}
            allColDefs={config.props?.options || [{ field: 'Public' }, { field: 'Private' }]}
            label={config.props?.label || (Array.isArray(value) && value.length > 1 ? 'Public & Private' : value?.[0] || 'Status')}
            controlContext={controlContext}
            menuContext={menuContext}
            {...config.props}
          />
        );

      case 'tableFields':
        return (
          <TableFieldsFilter
            selectedColumns={value || []}
            setSelectedColumns={handlers[`set${capitalize(stateKey)}`] || handlers[`setSelectedColumns`]}
            handleColumnsChange={handlers[`handle${capitalize(stateKey)}Change`] || handlers[`handleSelectedColumnsChange`]}
            allColDefs={config.props?.allColDefs || []}
            label={config.props?.label || 'Select'}
            controlContext={controlContext}
            menuContext={menuContext}
            {...config.props}
          />
        );

      case 'switch':
        return (
          <ControlSwitch
            context={controlContext}
            checked={config.props?.checked ?? false}
            onChange={config.props?.onChange}
            label={config.props?.label || ''}
            ariaLabel={config.props?.ariaLabel || config.props?.label || config.id}
            width={config.props?.width || config.width || 92}
          />
        );

      case 'iconSwitch':
        return (
          <IconSwitch
            checked={config.props?.checked ?? false}
            onChange={config.props?.onChange}
            labelOn={config.props?.labelOn || 'On'}
            labelOff={config.props?.labelOff || 'Off'}
            icon={config.props?.icon}
            width={config.props?.width || config.width || 100}
            showIcon={config.props?.showIcon ?? true}
            controlContext={controlContext}
          />
        );

      case 'simpleSelect':
        return (
          <SimpleSelect
            options={config.props?.options || []}
            value={value}
            onChange={handlers[`set${capitalize(stateKey)}`] || handlers[`handle${capitalize(stateKey)}Change`]}
            placeholder={config.props?.placeholder || 'Select...'}
            width={config.props?.width || config.width || 170}
            controlContext={controlContext}
            menuContext={menuContext}
            getOptionValue={config.props?.getOptionValue}
            getOptionLabel={config.props?.getOptionLabel}
            isPaid={isProUser}
            onRequirePro={onUpgradeClick}
            {...config.props}
          />
        );

      case 'chartCompany':
        // Single-select company filter for chart display
        const chartCompanyOptions = config.props?.options || [];
        return (
          <ControlAutocomplete
            context={controlContext}
            options={chartCompanyOptions}
            value={value}
            onChange={(_, newValue) => {
              const handler = handlers[`set${capitalize(stateKey)}`] || handlers[`setSelectedDashboardCompany`];
              if (handler) handler(newValue);
            }}
            getOptionLabel={(opt) => opt ? formatCompanyDisplayLabel({
              ticker: opt.ticker,
              companyName: opt.name,
            }) : ""}
            isOptionEqualToValue={(option, val) => option?.ticker === val?.ticker}
            minWidth={config.props?.minWidth || 220}
            placeholder={config.props?.placeholder || "Select Company"}
            renderOption={(props, opt) => (
              <li {...props} key={opt.ticker}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <TickerIcon ticker={opt.ticker} size={20} style={{ marginRight: 8 }} />
                  <span style={{ fontSize: '12px' }}>{opt.name}</span>
                  {!isPrivateCompanyIdentifier(opt.ticker) && (
                    <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary', fontSize: '11px' }}>
                      ({opt.ticker})
                    </Typography>
                  )}
                </Box>
              </li>
            )}
          />
        );

      case 'timeFrame':
        return (
          <ControlTimeFrameToggle
            context={controlContext}
            value={value}
            onChange={(e, newVal) => {
              const handler = handlers[`handle${capitalize(stateKey)}Change`] || handlers[`set${capitalize(stateKey)}`];
              if (handler && newVal !== null) handler(e, newVal);
            }}
            label={config.props?.label}
            labelWidth={config.props?.labelWidth ?? 0}
            buttonWidth={config.props?.buttonWidth}
            ariaLabel={config.props?.ariaLabel}
            {...config.props}
          />
        );

      case 'timeFramePeriod':
        return (
          <ControlPeriodToggle
            context={controlContext}
            value={value}
            onChange={(e, newVal) => {
              const handler = handlers[`handle${capitalize(stateKey)}Change`] || handlers[`set${capitalize(stateKey)}`];
              if (handler && newVal !== null) handler(e, newVal);
            }}
            label={config.props?.label}
            labelWidth={config.props?.labelWidth}
            buttonWidth={config.props?.buttonWidth}
            ariaLabel={config.props?.ariaLabel}
            {...config.props}
          />
        );

      case 'formatValues':
        return (
          <ControlFormatToggle
            context={controlContext}
            value={value}
            onChange={(e, newVal) => {
              const handler = handlers[`handle${capitalize(stateKey)}Change`] || handlers[`set${capitalize(stateKey)}`];
              if (handler && newVal !== null) handler(e, newVal);
            }}
            label={config.props?.label}
            labelWidth={config.props?.labelWidth}
            buttonWidth={config.props?.buttonWidth}
            ariaLabel={config.props?.ariaLabel}
            {...config.props}
          />
        );

      case 'calculationType':
        return (
          <ControlCalculationSelect
            context={controlContext}
            value={value}
            onChange={(newVal) => {
              const handler = handlers[`handle${capitalize(stateKey)}Change`] || handlers[`set${capitalize(stateKey)}`];
              if (handler) handler(newVal);
            }}
            metricField={config.props?.metricField}
            metricType={config.props?.metricType}
            isQuarterly={config.props?.isQuarterly}
            options={config.props?.options}
            width={config.props?.width}
            ariaLabel={config.props?.ariaLabel}
            {...config.props}
          />
        );

      case 'enhancedSlider':
        return (
          <EnhancedSlider
            context={controlContext}
            period={config.props?.period || 'Years'}
            sliderValue={value ?? 8}
            maxSliderValue={config.props?.max || 20}
            handleSliderChange={handlers.handleSliderChange || handlers[`handle${capitalize(stateKey)}Change`]}
            handleInputChange={handlers.handleInputChange}
            handleBlur={handlers.handleBlur}
            periods={config.props?.periods || []}
            periodFilter={state.periodFilter}
            onFilterChange={handlers.onFilterChange}
            width={width === 'auto' ? undefined : width}
            {...config.props}
          />
        );

      case 'assetClassFilter':
        return (
          <AssetClassFilter
            selectedItems={value || []}
            handleChange={handlers[`handleAssetClassSelectionChange`] || handlers[`handle${capitalize(stateKey)}Change`]}
            width={config.props?.width || 280}
          />
        );

      case 'custom':
        // For custom filters, expect the component to be passed in props
        if (config.props?.component) {
          const CustomComponent = config.props.component;
          return (
            <CustomComponent
              value={value}
              onChange={handlers[`set${capitalize(stateKey)}`]}
              isProUser={isProUser}
              onUpgradeClick={onUpgradeClick}
              {...config.props}
            />
          );
        }
        // If children are passed, render them directly
        if (config.props?.children) {
          return config.props.children;
        }
        return null;

      default:
        console.warn(`Unknown filter type: ${config.type}`);
        return null;
    }
  };

  return (
    <ControlField
      context={controlContext}
      width={fieldWidth}
      align={shouldShowSublabel ? 'center' : 'flex-start'}
      sx={{
        flexDirection: shouldShowSublabel ? 'column' : 'row',
        minHeight: shouldShowSublabel ? tokens.height + 12 : tokens.height,
        ...(config.sx || {}),
      }}
    >
      {renderFilter()}
      {shouldShowSublabel && (
        <Box data-filter-sublabel="true" sx={filterSublabelSx(theme)}>{config.sublabel}</Box>
      )}
    </ControlField>
  );
}

/**
 * Capitalize first letter of a string
 */
function capitalize(str) {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export default FilterRenderer;
